import { BackgroundCheckColumns } from '@/backend/features/background-check/shared';
import { BackgroundCheckType } from '@/backend/features/background-check/types';
import { useBackgroundCheckStorage } from '@/ui/features/background-check/hooks';
import { handleErrorSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { Filter } from '@juvare/platform-shared/features/storage';
import React from 'react';
import { useSelector } from 'react-redux';
import useSWR from 'swr';

export const useSelectedPersonBackgroundCheckData = (personId?: string, additionalFilters?: Filter<BackgroundCheckColumns>[]) => {
    const storageClient = useBackgroundCheckStorage();
    const { backgroundCheckConfig } = useSelector(getSettings);
    const id = React.useId();

    const key = additionalFilters ? `background-check-account-${personId}-${id}` : `background-check-account-${personId}`;

    const {
        data: backgroundCheckListData,
        isLoading,
        mutate: reload,
    } = useSWR(
        [key, personId, id],
        () => {
            if (!personId) {
                return { rows: [], total: 0 };
            }

            try {
                return storageClient.list({
                    orderBy: [{ column: 'updatedAt', desc: true }],
                    filter: [
                        {
                            column: 'accountId',
                            op: '=',
                            value: personId ?? '',
                        },
                        ...(additionalFilters ?? []),
                    ],
                });
            } catch (error) {
                handleErrorSnackbar(error);

                return { rows: [], total: 0 };
            }
        },
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        },
    );

    const backgroundCheckData = backgroundCheckListData?.rows;

    const enabledBackgroundCheckData = backgroundCheckData?.filter(
        (backgroundCheck) => backgroundCheckConfig.type[backgroundCheck.columns.type as BackgroundCheckType]?.enabled,
    );

    const sortedBackgroundCheckData = enabledBackgroundCheckData?.sort((a, b) => {
        const aSortOrder = backgroundCheckConfig.type[a.columns.type as BackgroundCheckType].displayOrder;
        const bSortOrder = backgroundCheckConfig.type[b.columns.type as BackgroundCheckType].displayOrder;

        return aSortOrder - bSortOrder;
    });

    return React.useMemo(
        () => ({
            backgroundCheckData: sortedBackgroundCheckData,
            isLoading,
            reload,
        }),
        [personId, backgroundCheckData, backgroundCheckConfig, isLoading, id, additionalFilters],
    );
};
